import { Component, effect, inject, input, signal } from '@angular/core';
import { NonNullableFormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DokaModal, DokaModalController, DokaTextField, DokaButton } from '@doka/angular-core';
import { I18NextEagerPipe } from 'angular-i18next';
import {
  CatalogClient,
  CatalogCategoryModel,
  EditCatalogCategoryModel,
  IEditCatalogCategoryModel,
  TranslationsClient,
} from 'src/api/services/generated/api-clients';
import { EditCatalogCategoryFormType } from './edit-catalog-category.form.type';
import { firstValueFrom } from 'rxjs';
import { rxResource } from '@angular/core/rxjs-interop';
import {
  atLeastOneCatalogNameTranslationValidator,
  CatalogNameTranslationFormType,
  getCatalogNameTranslationLanguageCodes,
  setCatalogNameTranslationControls,
  toCatalogNameTranslationModels,
} from '../catalog-name-translations.form.type';

@Component({
  templateUrl: 'edit-catalog-category-slide-over.component.html',
  imports: [DokaModal, I18NextEagerPipe, ReactiveFormsModule, DokaTextField, DokaButton],
})
export class EditCatalogCategorySlideOverComponent {
  private readonly catalogClient = inject(CatalogClient);
  private readonly translationsClient = inject(TranslationsClient);
  private readonly modalCtrl = inject(DokaModalController);
  private readonly fb = inject(NonNullableFormBuilder);

  protected isLoading = signal(false);
  public readonly category = input.required<CatalogCategoryModel>();

  protected readonly languages = rxResource({
    stream: () => this.translationsClient.getLanguages(),
  });

  protected readonly form: FormGroup<EditCatalogCategoryFormType> = this.fb.group({
    nameTranslations: this.fb.array<FormGroup<CatalogNameTranslationFormType>>(
      [],
      [atLeastOneCatalogNameTranslationValidator],
    ),
  });

  protected readonly nameTranslations = this.form.controls.nameTranslations;

  protected categoryEffect = effect(() => {
    const cat = this.category();
    if (cat) {
      if (!this.languages.value()?.data && this.languages.isLoading()) {
        return;
      }

      const languageCodes = getCatalogNameTranslationLanguageCodes(
        this.languages.value()?.data?.map((language) => language.languageCode),
        cat.nameTranslations,
      );

      setCatalogNameTranslationControls(this.nameTranslations, this.fb, languageCodes, cat.nameTranslations);
    }
  });

  protected closeModal() {
    this.modalCtrl.dismiss();
  }

  protected async submit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value: IEditCatalogCategoryModel = {
      nameTranslations: toCatalogNameTranslationModels(this.nameTranslations),
    };

    this.isLoading.set(true);
    try {
      await firstValueFrom(
        this.catalogClient.editCatalogCategory(this.category().id, new EditCatalogCategoryModel(value)),
      );

      this.modalCtrl.dismiss(true, 'updated');
    } catch (error) {
      console.error('Error editing catalog category', error);
    } finally {
      this.isLoading.set(false);
    }
  }
}
